"use client";

import React from "react";
import { ArrowRight, Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
export default function Cta() {
  const shouldReduceMotion = useReducedMotion();
  const nearbyAreas = [{
    name: "Richmond",
    to: "/AreasWeCoverPage"
  }, {
    name: "Wandsworth",
    to: "/AreasWeCoverPage"
  }];
  return <section id="finalcta" data-nav="light" className="relative w-full bg-background py-16 md:py-24 text-foreground overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="relative">
          <Card className="border border-border/80 bg-secondary text-secondary-foreground shadow-xl rounded-2xl overflow-hidden p-8 sm:p-12 lg:p-14">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              {/* Left Column: Headline & Action Buttons */}
              <div className="lg:col-span-7 space-y-6">
                <div className="inline-flex items-center gap-2 rounded-md bg-secondary-foreground/10 px-3 py-1.5 text-xs font-semibold tracking-wide text-secondary-foreground">
                  <ShieldCheck className="size-4 text-primary-foreground" />
                  <span>NASC &amp; CHAS accredited</span>
                </div>

                <div className="space-y-3">
                  <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-secondary-foreground">
                    Request a scaffolding quote
                  </h2>
                  <p className="text-secondary-foreground/80 text-base sm:text-lg max-w-xl leading-relaxed">
                    Speak with our team to arrange a prompt site survey and clear pricing for your upcoming commercial, residential, or roofing project.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-4 pt-2">
                  <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-6 py-3 font-semibold shadow-md transition-[color,background-color,box-shadow]">
                    <Link to="/ContactRequestQuotePage">
                      Request quote online
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>

                  <a href="tel:01344929900" className="inline-flex items-center justify-center gap-2 rounded-lg border border-secondary-foreground/20 bg-secondary px-5 py-2.5 text-sm font-semibold text-secondary-foreground hover:text-secondary-foreground hover:bg-secondary-foreground/10 transition-colors">
                    <Phone className="size-4 text-secondary-foreground" />
                    <span className="text-secondary-foreground">01344 929900</span>
                  </a>
                </div>
              </div>

              {/* Right Column: Direct Contact Details & Email */}
              <div className="lg:col-span-5 bg-card text-card-foreground p-6 sm:p-8 rounded-xl border border-border shadow-sm space-y-5">
                <h3 className="font-default text-xl font-bold text-foreground">
                  Direct enquiry channels
                </h3>
                <p className="text-sm text-muted-foreground leading-normal">
                  Send your drawings, specifications, or location details directly for a fast turnaround.
                </p>

                <div className="space-y-3.5 pt-1">
                  <a href="tel:01344929900" className="flex items-center gap-3.5 p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-accent/50 transition-colors group text-foreground hover:text-foreground">
                    <div className="size-10 rounded-md bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors shrink-0">
                      <Phone className="size-5" />
                    </div>
                    <div>
                      <span className="block text-xs font-medium text-muted-foreground">Telephone</span>
                      <span className="text-base font-semibold text-foreground">01344 929900</span>
                    </div>
                  </a>

                  <a href="mailto:quotes@bmfscaffolding.co.uk" className="flex items-center gap-3.5 p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-accent/50 transition-colors group text-foreground hover:text-foreground">
                    <div className="size-10 rounded-md bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors shrink-0">
                      <Mail className="size-5" />
                    </div>
                    <div className="min-w-0">
                      <span className="block text-xs font-medium text-muted-foreground">Email estimates</span>
                      <span className="text-sm sm:text-base font-semibold text-foreground break-all">
                        quotes@bmfscaffolding.co.uk
                      </span>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            {/* Bottom Strip: Nearby Areas */}
            <div className="mt-10 pt-8 border-t border-secondary-foreground/15">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-sm">
                <div className="flex flex-wrap items-center gap-2 text-secondary-foreground/80">
                  <MapPin className="size-4 text-secondary-foreground shrink-0" />
                  <span className="font-semibold text-secondary-foreground">Nearby areas:</span>
                  <div className="inline-flex items-center gap-2">
                    {nearbyAreas.map((area, i) => <React.Fragment key={area.name}>
                        <Link data-index={i} to={area.to} className="text-secondary-foreground hover:underline underline-offset-4 font-medium transition-colors">
                          {area.name}
                        </Link>
                        {i < nearbyAreas.length - 1 && <span className="text-secondary-foreground/40">•</span>}
                      </React.Fragment>)}
                  </div>
                </div>

                <div className="text-xs text-secondary-foreground/70">
                  Covering London, Surrey, Berkshire &amp; Home Counties
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </section>;
}